import React, { useState, useCallback, useEffect } from 'react';
import { styles, authCodeFormUtils } from '../styles/components/AuthCodeForm.styles.js';
import '../styles/components/AuthCodeForm.css';
import { useRegistrationFlow } from '../context/RegistrationFlowContext';
import { 
  STEPS, 
  STEP_INFO, 
  ERROR_MESSAGES, 
  SUCCESS_MESSAGES,
  validateField,
  inputHandlers,
  getProgressWidth 
} from '../utils/authUtils.js';
import { 
  sendVerificationCode,
  verifyCode,
  resendVerificationCode 
} from '../services/authService.js';
import { useNavigationBlock } from '../hooks/useNavigationBlock';
import NavigationBlockModal from '../components/ui/NavigationBlockModal';
import { ROUTES } from '../config/routes';

/**
 * 認証コードフォームコンポーネント
 * メール認証とコード検証の2段階認証を実装
 * @component
 * @param {Object} props - コンポーネントのプロパティ
 * @param {Function} props.onAuthComplete - 認証完了時のコールバック関数（メールアドレスを引数として受け取る）
 * @returns {JSX.Element} 認証フォームのJSX要素
 */
const AuthCodeForm = ({ onAuthComplete }) => {
  // ステート管理
  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL_INPUT);
  const [formData, setFormData] = useState({ email: '', verificationCode: '' });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [isNavigationAllowed, setIsNavigationAllowed] = useState(false);
  const [isBlockerReady, setIsBlockerReady] = useState(false);

  const { completeStep, REGISTRATION_STEPS } = useRegistrationFlow();

  const shouldBlockNavigation = isBlockerReady && !isNavigationAllowed && !isLoading;

  const { isModalOpen, handleConfirm, handleCancel } = useNavigationBlock(
    shouldBlockNavigation,
    null,
    ROUTES.TERMS
  );

  // コンポーネントマウント後、少し遅延させてブロッカーを有効化
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsBlockerReady(true);
    }, 300); // 300msの遅延
    
    return () => clearTimeout(timer);
  }, []);

  /**
   * メッセージを表示する関数
   * @function showMessage
   * @param {string} text - 表示するメッセージテキスト
   * @param {string} [type='info'] - メッセージタイプ（'info', 'success', 'error'）
   * @description 
   */
  const showMessage = useCallback((text, type = 'info') => {
    setMessage({ text, type });
    if (type === 'success') setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  }, []);

  /**
   * API実行をラップする共通関数
   * @function executeApiAction
   * @param {Function} apiFunction - 実行するAPI関数（sendVerificationCode, verifyCode, resendVerificationCode）
   * @param {...any} args - API関数に渡す引数
   * @returns {Promise<Object>} API実行結果
   * @description
   */
  const executeApiAction = useCallback(async (apiFunction, ...args) => {
    setIsLoading(true);
    try {
      return await apiFunction(...args);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * メールアドレス認証完了処理
   */
  const handleEmailVerification = useCallback((email) => {
    // ローカルステップも更新
    setCurrentStep(STEPS.CODE_VERIFICATION);
  }, []);


  /**
   * 成功画面のボタンクリック時の処理
   * ユーザーが明示的にボタンを押した時のみ次の画面に遷移
   */
  const handleSuccessButtonClick = useCallback(() => {

    // ナビゲーションを許可してから遷移
    setIsNavigationAllowed(true);
  
    completeStep(REGISTRATION_STEPS.EMAIL_VERIFICATION, {
      email: formData.email,
      emailVerified: true
    });

    // ナビゲーションブロックを解除してから遷移
    setTimeout(() => {
      if (onAuthComplete) {
        onAuthComplete(formData.email);
      }
    }, 100);
  }, [onAuthComplete, formData.email, completeStep, REGISTRATION_STEPS]);

  /**
   * フォーム送信処理のメインハンドラー
   * 
   * @function handleSubmit
   * @param {Event} e - フォーム送信イベント
   * @description
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const isEmailStep = currentStep === STEPS.EMAIL_INPUT;
    const currentField = isEmailStep ? 'email' : 'verificationCode';
    const currentValue = formData[currentField];
    
    // バリデーション
    const validationError = validateField(currentField, currentValue);
    if (validationError) {
      showMessage(validationError, 'error');
      setFieldError(validationError);
      return;
    }
    
    try {
      // API実行
      const apiFunction = isEmailStep ? sendVerificationCode : verifyCode;
      const args = isEmailStep ? [formData.email] : [formData.email, formData.verificationCode];
      const result = await executeApiAction(apiFunction, ...args);

      // 結果処理 - 失敗の場合
      if (!result.success) {
        const errorMessage = (currentStep === STEPS.CODE_VERIFICATION && ['verification', 'api'].includes(result.type)) 
          ? ERROR_MESSAGES.VERIFY_FAILED 
          : result.error || (isEmailStep ? ERROR_MESSAGES.SEND_FAILED : ERROR_MESSAGES.VERIFY_FAILED);
        showMessage(errorMessage, 'error');
        return;
      }

      // 結果処理 - 成功の場合（メール送信）
      if (isEmailStep) {
        showMessage(result.message || SUCCESS_MESSAGES.CODE_SENT, 'success');
        handleEmailVerification(formData.email);
        return;
      }

      // 結果処理 - 成功の場合（コード検証）
      // ここでAPI検証が成功した場合のみ成功画面に遷移
      showMessage(result.message || SUCCESS_MESSAGES.AUTH_COMPLETE, 'success');
      
      // ローカルステップを成功画面に更新（ここで止まる）
      setCurrentStep(STEPS.SUCCESS);
      
      // 重要：completeStepはhandleSuccessButtonClickで呼ぶ
      // これによりRegistrationRouteGuardによる自動リダイレクトを防ぐ
      
    } catch (error) {
      console.error('[AUTH CODE] Submit error:', error);
      showMessage('予期しないエラーが発生しました。もう一度お試しください。', 'error');
    }
  }, [currentStep, formData, executeApiAction, showMessage, handleEmailVerification]);

  /**
   * 認証コード再送信処理ハンドラー
   * @function handleResend
   * @description
   */
  const handleResend = useCallback(async () => {
    const validationError = validateField('email', formData.email);
    if (validationError) {
      showMessage(ERROR_MESSAGES.EMAIL_REQUIRED, 'error');
      return;
    }
    
    const result = await executeApiAction(resendVerificationCode, formData.email);
    showMessage(
      result.success ? (SUCCESS_MESSAGES.CODE_RESENT || result.message) : (result.error || ERROR_MESSAGES.RESEND_FAILED), 
      result.success ? 'success' : 'error'
    );
  }, [formData.email, executeApiAction, showMessage]);

  /**
   * 入力フィールドの変更処理ハンドラー
   * @function handleInputChange
   * @param {Event} e - 入力変更イベント
   * @param {string} e.target.name - 変更されたフィールド名（'email' or 'verificationCode'）
   * @param {string} e.target.value - 新しい入力値
   * @description
   */
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    const updateValue = (normalizedValue) => {
      setFormData(prev => ({ ...prev, [name]: normalizedValue }));
      const error = normalizedValue.length > 0 ? validateField(name, normalizedValue) : '';
      setFieldError(error || '');
    };
    
    inputHandlers[name] ? inputHandlers[name](value, updateValue) : updateValue(value);
    
    if (message.type === 'error') setMessage({ text: '', type: '' });
  }, [message.type]);

  // 現在のステップ情報と表示用変数を計算
  const stepInfo = STEP_INFO[currentStep];
  const isEmailStep = currentStep === STEPS.EMAIL_INPUT;
  const currentField = isEmailStep ? 'email' : 'verificationCode';
  const currentValue = formData[currentField];

  return (
    <>
      <div style={styles.body}>
        <div style={{...styles.container}} className="auth-code-container">
          {/* プログレスバー */}
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: getProgressWidth(currentStep) }}></div>
          </div>

          {/* メッセージ */}
          {message.text && (
            <div style={authCodeFormUtils.getMessageStyle(message)}>
              {message.text}
            </div>
          )}

          {/* ステップヘッダー */}
          <div style={styles.stepIndicator} className="auth-step-indicator">
            {`${stepInfo.icon} ${stepInfo.title}`}
          </div>

          {currentStep === STEPS.SUCCESS ? (
            // 成功画面
            <div style={styles.successAnimation}>
              <div style={styles.successIcon}>✓</div>
              <h2 style={styles.successTitle}>{SUCCESS_MESSAGES.AUTH_COMPLETE}</h2>
              <p style={{ marginBottom: '20px', color: '#333' }}>
                <strong>{formData.email}</strong><span> の認証が正常に完了しました。</span>
              </p>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                {stepInfo.description}
              </p>
              <button
                onClick={handleSuccessButtonClick}
                style={authCodeFormUtils.getButtonStyle(false, 'secondary')}
              >
                👤 お客様情報の入力へ進む
              </button>
            </div>
          ) : (
            // フォーム画面
            <>
              {!isEmailStep && (
                <div style={styles.emailDisplay} className="auth-email-display">
                  <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                    <span style={styles.required}>以下のメールアドレスに認証コードを送信しました。</span>
                  </p>
                  <strong style={{ color: '#09529f', fontSize: '18px' }}>
                    {formData.email}
                  </strong>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* 👇 テーブルからdivベースのレイアウトに変更 */}
                <div className="auth-form-group">
                  <label className="auth-form-label">
                    <span>{isEmailStep ? 'メールアドレス' : '認証コード'}</span>
                    <span className="required">*</span>
                  </label>
                  
                  <div className="auth-input-wrapper">
                    <input
                      type={isEmailStep ? 'email' : 'text'}
                      name={currentField}
                      value={currentValue}
                      onChange={handleInputChange}
                      placeholder={isEmailStep ? 'taro@example.com' : '000000'}
                      maxLength={isEmailStep ? undefined : 6}
                      className={!isEmailStep ? 'verification-code-input' : ''}
                      style={{
                        ...styles.input,
                        ...(fieldError && { 
                          borderColor: '#dc3545',
                          boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
                        })
                      }}
                      required
                    />
                    
                    {/* フィールドエラー */}
                    {fieldError && (
                      <div className="auth-field-error">
                        {fieldError}
                      </div>
                    )}
                    
                    <div className="auth-help-text">
                      {stepInfo.description}
                    </div>
                  </div>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <button 
                    type="submit"
                    className="auth-submit-button"
                    style={authCodeFormUtils.getButtonStyle(isLoading)} 
                    disabled={isLoading || fieldError}
                  >
                    {isLoading && <span style={styles.loading}></span>}
                    <span>{isLoading ? '処理中...' : (isEmailStep ? '認証コードを送信' : '認証して次へ進む')}</span>
                  </button>
                  
                  {!isEmailStep && (
                    <button
                      type="button"
                      onClick={handleResend}
                      style={{
                        ...styles.resendLink,
                        ...(isLoading && { opacity: 0.6, cursor: 'not-allowed' })
                      }}
                      disabled={isLoading}
                    >
                      認証コードを再送信
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <NavigationBlockModal
        isOpen={isModalOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default AuthCodeForm;